import { isString } from "lodash-es";

import defaultNotify from "@Components/notify/defaultNotify";

import { useNotifyStore } from "@Store/overhauling/useNotifyStore";

import useTranslation from "@Composables/useTranslation";

/**
 * Constructs the notification message.
 *
 * This function simplifies the notification process for components using the
 * `useNotify` composable. It allows notifications to be triggered with the following methods:
 *   1) `this.notifyError();` - Triggers a default error notification.
 *   2) `this.notifyError({ text: "Custom text" });` - Triggers a custom error notification with specified text.
 */
function buildMessage(options) {
  if (isString(options)) {
    return { text: options };
  }

  return options;
}

/**
 * This composable provides methods to display different types of notifications
 * such as error, info, and success messages, as well as handling copy events.
 */
export default function useNotify() {
  const { ADD_NOTIFY } = useNotifyStore();
  const { __ } = useTranslation();

  function buildOptions({ component, type, title, duration, text }) {
    const defaultOptions = defaultNotify();

    return {
      component: component ?? defaultOptions.component,
      type: type ?? defaultOptions.type,
      title: title ?? defaultOptions.title,
      duration: duration ?? defaultOptions.duration,
      text: text ?? defaultOptions.text,
    };
  }

  function notifyError(options) {
    const notify = buildOptions({
      type: "error",
      title: "Invalid",
      ...buildMessage(options),
    });

    ADD_NOTIFY(notify);
  }

  function notifyInfo(options) {
    const notify = buildOptions({
      type: "info",
      title: "Notice",
      ...buildMessage(options),
    });

    ADD_NOTIFY(notify);
  }

  function notifySuccess(options) {
    const notify = buildOptions({
      type: "success",
      title: "Success",
      ...buildMessage(options),
    });

    ADD_NOTIFY(notify);
  }

  function onCopySuccess() {
    notifySuccess({ text: __("clipboard_copy_success") });
  }

  function onCopyError() {
    notifyError({ text: __("clipboard_copy_fails") });
  }

  return {
    notifyError,
    notifyInfo,
    notifySuccess,
    onCopySuccess,
    onCopyError,
  };
}
